/** A calendar day and a wall-clock time, as read in one time zone. */
interface WallTime {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const DAY_MS = 86_400_000;

/** The wall-clock reading of an instant in a time zone. */
function wallTimeAt(instantMs: number, timeZone: string): WallTime {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).formatToParts(new Date(instantMs));
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((entry) => entry.type === type)?.value);
  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: part('hour'),
    minute: part('minute'),
  };
}

/** The wall time as if it were UTC, which turns the zone's offset into plain arithmetic. */
function asUtcMs(wall: WallTime): number {
  return Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
}

/** How far the zone's wall clock runs ahead of UTC at an instant. */
function offsetMs(instantMs: number, timeZone: string): number {
  return asUtcMs(wallTimeAt(instantMs, timeZone)) - instantMs;
}

/**
 * The instant a wall-clock time happens in a time zone. It tries the offsets in force a day before
 * and a day after: on an ordinary day they agree; a time a daylight saving change repeats takes its
 * first occurrence; a time the change skips moves forward by the gap (02:30 becomes 03:30), never
 * back to before the requested time.
 */
function instantOfWallTime(wall: WallTime, timeZone: string): number {
  const target = asUtcMs(wall);
  const offsetBefore = offsetMs(target - DAY_MS, timeZone);
  const offsetAfter = offsetMs(target + DAY_MS, timeZone);
  const matches = [target - offsetBefore, target - offsetAfter].filter(
    (instant) => asUtcMs(wallTimeAt(instant, timeZone)) === target,
  );
  return matches.length > 0 ? Math.min(...matches) : target - offsetBefore;
}

/** The calendar day `days` after the given one, as a wall time at `hour:minute`. */
function dayShifted(
  wall: WallTime,
  days: number,
  hour: number,
  minute: number,
): WallTime {
  const shifted = new Date(
    Date.UTC(wall.year, wall.month - 1, wall.day + days),
  );
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour,
    minute,
  };
}

/** Whether the runtime knows this IANA zone. */
function isKnownTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The daily slots around `nowMs`: `last` is the latest at or before it, `next` the first after it.
 * A slot is `timeOfDay` (`HH:MM`) on a local calendar day of `timeZone`, so days stay calendar days
 * across daylight saving changes. An unknown zone reads as UTC.
 */
export function dailySlotsMs(
  timeOfDay: string,
  timeZone: string,
  nowMs: number = Date.now(),
): { last: number; next: number } {
  const [hour = 0, minute = 0] = timeOfDay.split(':').map(Number);
  const zone = isKnownTimeZone(timeZone) ? timeZone : 'UTC';
  const today = wallTimeAt(nowMs, zone);
  const slotOn = (days: number): number =>
    instantOfWallTime(dayShifted(today, days, hour, minute), zone);
  const todaySlot = slotOn(0);
  return todaySlot <= nowMs
    ? { last: todaySlot, next: slotOn(1) }
    : { last: slotOn(-1), next: todaySlot };
}

/** The calendar day (`YYYY-MM-DD`) an instant falls on in a time zone; an unknown zone reads as UTC. */
export function calendarDayIn(instantMs: number, timeZone: string): string {
  const wall = wallTimeAt(
    instantMs,
    isKnownTimeZone(timeZone) ? timeZone : 'UTC',
  );
  return [wall.year, wall.month, wall.day]
    .map((part) => String(part).padStart(2, '0'))
    .join('-');
}

/** The instant `timeOfDay` (`HH:MM`) happens on a calendar day (`YYYY-MM-DD`) of a time zone. */
export function instantOnDayMs(
  calendarDay: string,
  timeOfDay: string,
  timeZone: string,
): number {
  const [year = 1970, month = 1, day = 1] = calendarDay.split('-').map(Number);
  const [hour = 0, minute = 0] = timeOfDay.split(':').map(Number);
  return instantOfWallTime(
    { year, month, day, hour, minute },
    isKnownTimeZone(timeZone) ? timeZone : 'UTC',
  );
}
